import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, DollarSign, CreditCard, BarChart2, Kanban, MonitorPlay, Clock, Package, FileText, CheckCircle2, Wrench, CalendarCheck, ShieldCheck, FileSpreadsheet, Timer, Users, Calculator, WifiOff, Lock, Mail, Server, GitBranch, CalendarRange } from "lucide-react";
import MessageForm from "../components/MessageForm";
import generatePDF from "../utils/generateCVPDF";
import LinkedInButton from "../components/LinkedInButton";

// ── Skill categories ──────────────────────────────────────────────────────────
const skills = [
  {
    category: "User & Endpoint Support",
    items: [
      "Windows 10/11 Setup & Troubleshooting",
      "Software Installation & Updates",
      "Printer, Driver & Peripheral Support",
      "Wi-Fi Troubleshooting",
      "Remote Support (AnyDesk, TeamViewer)"
    ],
  },
  {
    category: "Cloud & Productivity Platforms",
    items: [
      "Microsoft 365 (Entra ID, SharePoint, OneDrive, Teams, Outlook)",
      "Google Workspace Administration",
      "User Provisioning & Licence Assignment",
      "Access Permissions Management"
    ],
  },
  {
    category: "Networking & DNS",
    items: [
      "Basic Network Checks (IP/DNS/Gateway)",
      "IP Printer Setup",
      "A/MX/TXT DNS Records",
      "SPF/DKIM Setup & SSL Activation"
    ],
  },
  {
    category: "Ticketing & Documentation",
    items: [
      "JIRA Issue Logging",
      "Resolution Notes & Handover Docs",
      "Git Change Tracking",
      "Structured Troubleshooting Write-ups"
    ],
  }
];

const leadershipSignals = [
  {
    title: "Public Speaking & Facilitation",
    description: "Delivered talks, presentations, and facilitated discussions across community, school, and group settings, translating ideas clearly for different audiences.",
  },
  {
    title: "Leadership & Delegation",
    description: "Led volunteer and community initiatives, coordinated core team members, assigned roles, and improved follow-through through clearer responsibilities and structure.",
  },
  {
    title: "Stakeholder Communication",
    description: "Worked directly with staff, volunteers, parents, and community groups, combining plain-English communication with training, coordination, and operational support.",
  },
];

// ── Featured project ──────────────────────────────────────────────────────────
const featured = {
  title: "Sacramental Enrolment System",
  subtitle: "Production SaaS — ~100 active parents, first commercial KleinSoft contract, saving ~20 hours of admin per programme",
  description:
    "Production-grade enrolment management platform for St Patrick's Parish, Sutherland. Handles the full lifecycle from parent registration and session booking to admin approvals, email delivery, and audit logging — across multiple sacrament programmes. Built, deployed, and actively maintained as a commercial product under KleinSoft. I own all development, support, incidents, and client communication.",
  highlights: [
    { Icon: GitBranch,    label: "Transactional Booking",          detail: "Enrolments finalise via an atomic Cloud Function that generates a sequential reference (e.g. PAT-2026-R042), snapshots child data, and writes an audit log entry in a single batch — no partial writes possible." },
    { Icon: Lock,         label: "Role-Based Access Control",       detail: "4 roles (dev / admin / assistant / parent) enforced via Firebase custom claims and ~200 lines of Firestore security rules — parents can only touch their own data; admins are parish-scoped." },
    { Icon: Mail,         label: "Microsoft Graph Email",           detail: "Branded HTML emails via OAuth2 client credentials (MS Graph API) — bulk sends, per-enrolment emails, a template system with placeholder substitution, and a full email history log." },
    { Icon: Server,       label: "Multi-Environment Architecture",  detail: "4 environments (production, staging, demo, emulator) with a gateway layer that transparently switches data sources. Feature flags gate unreleased features per environment." },
  ],
  inDevelopment: [
    { Icon: CalendarRange, label: "Capacity-Managed Session Booking via Magic Link", detail: "After enrolment is confirmed, parents receive a time-limited email link to a standalone web app where they choose a Confirmation Mass session. A Firestore transaction enforces capacity limits in real time — if a session fills mid-submit, the UI prompts the parent to choose the other. No login required." },
    { Icon: FileText,      label: "Confirmation Card PDF",           detail: "Printable PDF card per child with saint name, sponsor, and session details — individual and bulk download for admins." },
  ],
  stack: [
    "React 19", "TypeScript", "Vite", "Tailwind CSS",
    "Firebase", "TanStack Query", "TanStack Table",
    "Microsoft Graph API", "Zod", "Playwright",
  ],
  images: ["/images/sacrament-system-image1.png", "/images/sacrament-system-image2.png"],
};

// ── Smaller projects ──────────────────────────────────────────────────────────
const otherProjects = [
  {
    title: "KleinSoft One — Business Operations Platform",
    description: "Full-stack business operations platform for small businesses and sole traders. Unifies clients, invoicing, payments, expenses, tasks, and services into a single authenticated web app with live Stripe integration.",
    stack: ["Next.js 15", "TypeScript", "Firebase", "Stripe API", "Tailwind CSS", "Radix UI", "Zustand", "Zod"],
    features: [
      { Icon: Zap,         label: "Live Stripe Sync",           detail: "Creating a client auto-provisions a Stripe Customer; defining a service creates a Stripe Product/Price via Cloud Functions." },
      { Icon: CreditCard,  label: "Unified Payments View",       detail: "Merges live Stripe invoices with legacy Firestore invoices with filter by source and status." },
      { Icon: BarChart2,   label: "Real-Time Financial Dashboard", detail: "Pulls paid, outstanding, and overdue totals directly from the Stripe API and Firestore." },
      { Icon: Kanban,      label: "Flexible Task Management",    detail: "Three views: Table, drag-and-drop Kanban board, and drag-and-drop Calendar (week/month)." },
    ],
    images: ["/images/kleinsoft-one-1.png"],
  },
  {
    title: "KleinSoft Ops — Field Operations App",
    description: "Purpose-built Android app for independent IT support technicians. Handles the full job lifecycle on a single device — from arrival to payment.",
    stack: ["Flutter", "Dart", "Firebase", "Stripe", "Cloud Functions", "Firestore", "Firebase App Check"],
    features: [
      { Icon: Users,      label: "Customer Management",        detail: "Searchable database with device details, visit history, and real-time cloud sync." },
      { Icon: Timer,      label: "Live Visit Timer",            detail: "On-site timer with start/pause/stop. Survives app backgrounding and device restarts — nothing lost mid-job." },
      { Icon: Calculator, label: "Automated Billing",           detail: "$60 flat for the first hour, then $15 per 15-minute increment. Calculated automatically." },
      { Icon: CreditCard, label: "Flexible Payment Collection", detail: "Stripe Tap to Pay, email invoice, or a payment link via SMS or WhatsApp — whatever suits the customer." },
    ],
    images: ["/images/kleinsoft-ops-1.png", "/images/kleinsoft-ops-2.png"],
  },
  {
    title: "Mass Slide Builder",
    description: "Python/Tkinter desktop tool built to automate a recurring operational workflow, reducing manual slide preparation and improving consistency across weekly services. Packaged as a standalone Windows installer for straightforward staff use in a live operational setting.",
    stack: ["Python", "Tkinter", "python-pptx", "python-docx", "PyInstaller", "Inno Setup"],
    features: [
      { Icon: MonitorPlay, label: "Automated Slide Generation",   detail: "Generates weekly presentation slides from structured service content automatically." },
      { Icon: Clock,       label: "Saved ~2 Hours Per Week",      detail: "Replaced a fully manual slide preparation process for ongoing weekly operational use." },
      { Icon: Package,     label: "Windows Installer (.exe)",     detail: "Packaged with PyInstaller and Inno Setup for straightforward installation by non-technical staff." },
      { Icon: FileText,    label: "Documented & Handed Over",     detail: "Produced user documentation and delivered hands-on training for ongoing staff use." },
    ],
    images: ["/images/mass-slide-builder-image1.png"],
  },
  {
    title: "Project Tracker Desktop",
    description: "Privacy-first desktop productivity tool that silently monitors active VS Code projects via the Win32 API and logs every coding session automatically — no manual timers, no cloud accounts, no data leaving the machine. Built out of a genuine need to track hours across multiple client projects and business entities.",
    stack: ["Python 3", "Tkinter / ttk", "ctypes / Win32 API", "tkcalendar", "JSON", "iCalendar (.ics)"],
    features: [
      { Icon: Clock,          label: "Zero-Input Time Tracking",     detail: "Background daemon polls the active window every 5s. Detects project switches, idle timeouts, and VS Code closures automatically." },
      { Icon: BarChart2,      label: "Real-Time Dashboard",          detail: "Live Tkinter GUI auto-refreshes every 5s, blending completed sessions with the in-progress live session for accurate totals." },
      { Icon: FileSpreadsheet, label: "Categories & Colour Coding",  detail: "Right-click any project to assign a business category (KleinSoft, Career, Volunteering…). Rows are colour-coded by category hash." },
      { Icon: CalendarCheck,  label: "Export to Calendar (.ics)",    detail: "RFC 5545-compliant ICS export groups sessions by project and date into calendar events, ready for Google Calendar or Outlook." },
    ],
    images: ["/images/project-tracker-desktop-1.png"],
  },
];

// ─────────────────────────────────────────────────────────────────────────────

const Home = () => {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  return (
    <div className="bg-gray-50 min-h-screen">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section id="hero" className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 pt-28 pb-16">
          <p className="text-xs font-semibold tracking-widest text-blue-600 uppercase mb-4">
            Seeking Digital Operations, Support, and Customer-Facing Roles
          </p>
          {/* Photo + name lockup */}
          <div className="flex items-center gap-5">
            <img
              src="/images/profile-photo.png"
              alt="Laurence Klein"
              className="w-24 h-24 rounded-full object-cover object-top flex-shrink-0 shadow-md ring-2 ring-gray-200"
            />
            <div>
              <h1 className="text-5xl font-bold text-gray-900 leading-tight">Laurence Klein</h1>
              <h2 className="text-xl text-gray-500 mt-1 font-normal">Digital Systems | Customer Experience | Process Improvement</h2>
            </div>
          </div>
            {/* Summary + CTA */}
            <p className="mt-6 text-gray-600 leading-relaxed max-w-2xl">
              Hands-on digital systems and customer-focused operations professional with nearly four years of experience spanning technical support, workflow improvement, bookings, reporting tools, website updates, and stakeholder communication. Currently completing Cert III IT and seeking opportunities across digital operations, customer experience, product support, junior business analysis, and customer-facing technology roles.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
            <Link
              to="/cv"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                <path fillRule="evenodd" d="M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Zm2 6a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5A.75.75 0 0 1 6 10Zm.75 2.75a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd" />
                <path d="M11 2.5V6a1 1 0 0 0 1 1h3.5L11 2.5Z" />
              </svg>
              View Full CV
            </Link>
            <button
              onClick={generatePDF}
              className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
                <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
              </svg>
              Download CV
            </button>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" />
                <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
              </svg>
              Get in Touch
            </a>
            <LinkedInButton />
            </div>
          </div>
          </section>

          {/* ── Featured Project ───────────────────────────────────────────────── */}
      <section id="projects" className="scroll-mt-16 max-w-5xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-8">Featured Project</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {/* Image — full width, top */}
          <div
            className="w-full bg-gray-50 cursor-zoom-in border-b border-gray-100"
            onClick={() => setLightboxSrc(featured.images[0])}
          >
            <img
              src={featured.images[0]}
              alt={featured.title}
              className="w-full object-contain hover:opacity-90 transition"
              style={{ maxHeight: "520px" }}
            />
          </div>

          {/* Content — below image */}
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900">{featured.title}</h3>
            <p className="text-xs text-blue-600 font-medium mt-1 mb-4">{featured.subtitle}</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">{featured.description}</p>

            {/* Highlights */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {featured.highlights.map(({ Icon, label, detail }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Icon size={14} className="text-blue-500" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 leading-snug">{label}</p>
                    <p className="text-xs text-gray-500 leading-snug mt-0.5">{detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* In development */}
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">In Development — Client Requested</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {featured.inDevelopment.map(({ Icon, label, detail }, i) => (
                <div key={i} className={`flex items-start gap-3 rounded-xl p-3 border ${i === 0 ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                  <span className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${i === 0 ? 'bg-amber-100' : 'bg-gray-200'}`}>
                    <Icon size={14} className={i === 0 ? 'text-amber-600' : 'text-gray-500'} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 leading-snug">{label}</p>
                    <p className="text-xs text-gray-500 leading-snug mt-0.5">{detail}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {featured.stack.map((s, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Other projects */}
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mt-14 mb-5">More Projects</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {otherProjects.map((p, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">

              {/* Image — full width or side-by-side for 2 images */}
              {p.images && p.images.length === 2 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 border-b border-gray-100">
                  {p.images.map((img, idx) => (
                    <div
                      key={idx}
                      className={`bg-gray-50 cursor-zoom-in ${idx === 0 ? "border-b sm:border-b-0 sm:border-r border-gray-100" : ""}`}
                      onClick={() => setLightboxSrc(img)}
                    >
                      <img
                        src={img}
                        alt={`${p.title} screenshot ${idx + 1}`}
                        className="w-full object-contain hover:opacity-90 transition"
                        style={{ maxHeight: "320px" }}
                      />
                    </div>
                  ))}
                </div>
              ) : p.images && p.images.length > 0 ? (
                <div
                  className="w-full bg-gray-50 cursor-zoom-in border-b border-gray-100"
                  onClick={() => setLightboxSrc(p.images[0])}
                >
                  <img
                    src={p.images[0]}
                    alt={`${p.title} screenshot`}
                    className="w-full object-contain hover:opacity-90 transition"
                    style={{ maxHeight: "420px" }}
                  />
                </div>
              ) : null}

              {/* Content — below image */}
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-900">{p.title}</h3>
                <p className="text-sm text-gray-500 mt-2 mb-4 leading-relaxed">{p.description}</p>
                <ul className="space-y-3 mb-4 flex-1">
                  {p.features.map(({ Icon, label, detail }, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Icon size={14} className="text-blue-500" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 leading-snug">{label}</p>
                        <p className="text-xs text-gray-500 leading-snug mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-1.5 mt-auto">
                  {p.stack.map((s, j) => (
                    <span key={j} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Leadership & Communication ────────────────────────────────────── */}
      <section id="leadership" className="scroll-mt-16 max-w-5xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-8">Leadership & Communication</p>
        <div className="grid md:grid-cols-3 gap-4">
          {leadershipSignals.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Skills ────────────────────────────────────────────────────────── */}
      <section id="capabilities" className="scroll-mt-16 bg-white border-t border-b border-gray-200 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-8">Technical Foundations</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {skills.map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3">{s.category}</p>
                <ul className="space-y-1.5">
                  {s.items.map((item, j) => (
                    <li key={j} className="text-xs text-gray-500 leading-snug">{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ───────────────────────────────────────────────────────── */}
      <section id="contact" className="scroll-mt-16 max-w-5xl mx-auto px-6 py-16">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Contact</p>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Get in touch</h2>
        <p className="text-gray-500 text-sm mb-8">Fill in the form and I'll get back to you shortly.</p>
        <div className="max-w-2xl">
          <MessageForm />
        </div>
      </section>

      {/* ── Lightbox ─────────────────────────────────────────────────────── */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-5 text-white text-3xl leading-none hover:text-gray-300 transition"
          >
            &times;
          </button>
          <img
            src={lightboxSrc}
            alt="Enlarged view"
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </div>
  );
};

export default Home;
